import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Select, Button, Input, Dropdown, Modal } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { useLayerProperitesContext } from '../../../../context/LayerPropertiesContext';
import SymbologySwatch from './components/SymbologySwatch';
import CategorySymbologyEditor from './CategorySymbologyEditor';
import ConditionalRender from '../../../../shared/components/ConditionalRender';
import { useAuth } from '../../../../context/AuthContext';
import { LayerAction } from '../../../../enums/mapPortalActions';
import { layerActionAccess } from '../../constants';
import { SingleSymbologyConfig, UniqueSymbologyConfig } from '../../../../types/symbology.types';
import {
  buildSingleConfig,
  makeCategory,
  reconcileCategories,
  unsymbolizedValues,
  MAX_DISPLAY_LABEL,
  DISPLAY_LABEL_ERROR,
} from './symbologyConfig.utils';
import '../LayerProperties.scss';

interface UniqueSymbologyProps {
  keyOptions: { label: string; value: string }[];
  /** field -> array of unique values mapping for this layer. */
  features: Record<string, (string | number)[]>;
  /** Open the shared Choose-from-styles / Upload flow for a category icon. */
  onRequestExternalIcon: (source: 'styles' | 'upload') => Promise<{ key: string } | null>;
}

const UniqueSymbology: React.FC<UniqueSymbologyProps> = ({ keyOptions, features, onRequestExternalIcon }) => {
  const ctx = useLayerProperitesContext();
  const { symbologyConfig, layerType, setState } = ctx;

  const isPointLayer = layerType === 'Point' || layerType === 'MultiPoint';
  const { canAccess } = useAuth();
  const canEditLayer = canAccess(layerActionAccess(LayerAction.EDIT_LAYER_PROPERTIES));

  // The current unique config (or an empty one). Source of truth lives in
  // context.symbologyConfig so it round-trips to the backend JSONB column.
  const uniqueConfig: UniqueSymbologyConfig = useMemo(
    () => (symbologyConfig && symbologyConfig.type === 'unique' ? symbologyConfig : { type: 'unique', field: '', categories: [] }),
    [symbologyConfig],
  );

  // Default symbology for newly-created categories = the layer's currently
  // configured Single symbology (US-101663 Scenario 3).
  const baseSingleConfig: SingleSymbologyConfig = useMemo(() => buildSingleConfig(ctx), [ctx]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<Set<string>>(new Set());
  const [labelDrafts, setLabelDrafts] = useState<Record<number, string>>({});

  const datasetValues = useMemo(() => {
    const field = uniqueConfig.field;
    if (!field || !features?.[field]) return [];
    return features[field].filter((v) => v !== null && v !== undefined);
  }, [features, uniqueConfig.field]);

  const commitUnique = (next: UniqueSymbologyConfig) => {
    setState((prev) => ({
      ...prev,
      symbology: 'unique',
      symbologyConfig: next,
      // Mirror the field for any legacy reads; categories themselves only
      // live in symbologyConfig.
      symbologyUniqueKey: next.field || undefined,
    }));
  };

  // US-101663 Scenario 8: changing the field clears the table and regenerates
  // categories from the new field's unique values (discarding unsaved edits).
  // Re-selecting the SAME field is a no-op so previously configured (and
  // saved) per-category icons/styles are preserved.
  //
  // The distinct values are now fetched server-side (scales with layer size), so
  // they arrive AFTER the field is set. We record the field in pendingGenRef and
  // let the effect below build the categories once its values load.
  const pendingGenRef = useRef<string | null>(null);
  const handleFieldChange = (field: string) => {
    if (field === uniqueConfig.field) return;
    pendingGenRef.current = field;
    setEditingIndex(null);
    setLabelDrafts({});
    commitUnique({ type: 'unique', field, categories: [] });
  };

  // Build categories once the freshly-selected field's distinct values arrive.
  // Guarded by pendingGenRef so loading a SAVED config (field already set, with
  // categories) never regenerates and clobbers per-category styling.
  useEffect(() => {
    if (!uniqueConfig.field || pendingGenRef.current !== uniqueConfig.field) return;
    if (datasetValues.length === 0) return; // values still loading
    pendingGenRef.current = null;
    const categories = datasetValues.map((v) => makeCategory(v, baseSingleConfig));
    commitUnique({ type: 'unique', field: uniqueConfig.field, categories });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueConfig.field, datasetValues]);

  // US-101664 Scenarios 11 & 12: after a dataset re-upload, reconcile saved
  // categories against the values that still exist. Run once per (field,
  // dataset) change; never auto-adds new values.
  const reconciledRef = useRef<string>('');
  useEffect(() => {
    if (!uniqueConfig.field || datasetValues.length === 0) return;
    const signature = `${uniqueConfig.field}::${datasetValues.map(String).sort().join('|')}`;
    if (reconciledRef.current === signature) return;
    reconciledRef.current = signature;
    const reconciled = reconcileCategories(uniqueConfig.categories, datasetValues);
    if (reconciled.length !== uniqueConfig.categories.length) {
      commitUnique({ ...uniqueConfig, categories: reconciled });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uniqueConfig.field, datasetValues]);

  const updateCategoryLabel = (index: number, raw: string) => {
    setLabelDrafts((prev) => ({ ...prev, [index]: raw }));
    // US-101663 Scenario 6: values over 50 chars are not saved (error shown).
    // Scenario 7: a blank value is allowed (falls back to the raw value).
    if (raw.length <= MAX_DISPLAY_LABEL) {
      const categories = uniqueConfig.categories.map((c, i) => (i === index ? { ...c, displayLabel: raw } : c));
      commitUnique({ ...uniqueConfig, categories });
    }
  };

  const removeCategory = (index: number) => {
    // US-101664 Scenario 5: the category is removed; its features are no
    // longer symbolized (the SLD simply omits a rule for that value).
    if (editingIndex === index) setEditingIndex(null);
    const categories = uniqueConfig.categories.filter((_, i) => i !== index);
    setLabelDrafts({});
    commitUnique({ ...uniqueConfig, categories });
  };

  const applyToCategory = (index: number, config: SingleSymbologyConfig) => {
    const categories = uniqueConfig.categories.map((c, i) => (i === index ? { ...c, symbology: config } : c));
    commitUnique({ ...uniqueConfig, categories });
    setEditingIndex(null);
  };

  const applyToAll = (config: SingleSymbologyConfig) => {
    const categories = uniqueConfig.categories.map((c) => ({ ...c, symbology: { ...config } }));
    commitUnique({ ...uniqueConfig, categories });
    setEditingIndex(null);
  };

  // --- Add Category popup ---
  const addPool = useMemo(() => unsymbolizedValues(uniqueConfig.categories, datasetValues), [uniqueConfig.categories, datasetValues]);

  const togglePending = (value: string) => {
    setPendingAdd((prev) => {
      const next = new Set(prev);
      if (next.has(value))
        next.delete(value); // Scenario 10: clicking + again deselects
      else next.add(value);
      return next;
    });
  };

  const confirmAddCategories = () => {
    // Preserve the original value type (number vs string) from the dataset.
    const byKey = new Map(datasetValues.map((v) => [String(v), v]));
    const added = [...pendingAdd].map((k) => makeCategory(byKey.get(k) ?? k, baseSingleConfig));
    commitUnique({ ...uniqueConfig, categories: [...uniqueConfig.categories, ...added] });
    setPendingAdd(new Set());
    setShowAddPopup(false);
  };

  const editingCategory = editingIndex != null ? uniqueConfig.categories[editingIndex] : null;

  return (
    <div className="lp-symbology__stack">
      <div className="lp-symbology__card">
        {/* Field */}
        <div className="lp-unique__field-row">
          <span className="lp-unique__field-label">FIELD</span>
          <Select className="lp-controls__flex" placeholder="Select Field" value={uniqueConfig.field || undefined} onChange={handleFieldChange}>
            {keyOptions?.map((key) => (
              <Select.Option key={key.value} value={key.value}>
                {key.value}
              </Select.Option>
            ))}
          </Select>
        </div>

        {/* Category table */}
        {uniqueConfig.field && (
          <div className="lp-unique__table">
            <div className="lp-unique__row lp-unique__row--head">
              <span className="lp-unique__col-symbol">Symbol</span>
              <span className="lp-unique__col-value">Value</span>
              <span className="lp-unique__col-display">Display</span>
              <span className="lp-unique__col-remove" />
            </div>

            {uniqueConfig.categories.map((category, index) => {
              const draft = labelDrafts[index];
              const labelValue = draft !== undefined ? draft : category.displayLabel;
              const tooLong = labelValue.length > MAX_DISPLAY_LABEL;
              return (
                <div className="lp-unique__row" key={`${String(category.value)}-${index}`}>
                  <button
                    type="button"
                    className="lp-unique__col-symbol lp-unique__symbol-btn"
                    onClick={() => setEditingIndex(index)}
                    title="Edit category symbology"
                  >
                    <SymbologySwatch symbology={category.symbology} isPointLayer={isPointLayer} />
                  </button>
                  <span className="lp-unique__col-value" title={String(category.value)}>
                    {String(category.value)}
                  </span>
                  <span className="lp-unique__col-display">
                    <Input
                      value={labelValue}
                      status={tooLong ? 'error' : undefined}
                      onChange={(e) => updateCategoryLabel(index, e.target.value)}
                      placeholder={String(category.value)}
                    />
                    {tooLong && <div className="lp-unique__display-error">{DISPLAY_LABEL_ERROR}</div>}
                  </span>
                  <ConditionalRender visible={canEditLayer}>
                    <button
                      type="button"
                      className="lp-unique__col-remove lp-unique__remove-btn"
                      onClick={() => removeCategory(index)}
                      title="Remove category"
                    >
                      <CloseOutlined />
                    </button>
                  </ConditionalRender>
                </div>
              );
            })}
          </div>
        )}

        {/* US-101664 Scenarios 7-10: Add Category — an inline dropdown
            anchored under the button (per Figma). Sits below the table card. */}
        <ConditionalRender visible={!!uniqueConfig.field && addPool.length > 0 && canEditLayer}>
          <Dropdown
            open={showAddPopup}
            trigger={['click']}
            placement="bottomLeft"
            onOpenChange={(next) => {
              setShowAddPopup(next);
              if (next) setPendingAdd(new Set());
            }}
            dropdownRender={() => (
              <div className="lp-unique__add-panel">
                <div className="lp-unique__add-item-container">
                  {addPool.map((value) => {
                    const key = String(value);
                    const selected = pendingAdd.has(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`lp-unique__add-item${selected ? ' lp-unique__add-item--selected' : ''}`}
                        onClick={() => togglePending(key)}
                      >
                        <PlusOutlined />
                        <span>{key}</span>
                      </button>
                    );
                  })}
                </div>
                <div className="lp-unique__add-done">
                  <Button onClick={confirmAddCategories}>Done</Button>
                </div>
              </div>
            )}
          >
            <Button className="lp-unique__add-btn" icon={<PlusOutlined />}>
              Add category
            </Button>
          </Dropdown>
        </ConditionalRender>
      </div>

      {/* US-101664 Scenario 1: clicking a category's Symbol opens its
          symbology editor as a modal (icon selection + fill + stroke). */}
      <Modal
        open={editingCategory != null}
        onCancel={() => setEditingIndex(null)}
        footer={null}
        destroyOnClose
        width={560}
        styles={{
          body: {
            maxHeight: '80vh',
            overflowY: 'auto',
          },
        }}
        centered
        className="lp-category-editor__modal"
      >
        {editingCategory && (
          <CategorySymbologyEditor
            key={editingIndex}
            title={editingCategory.displayLabel.trim() || String(editingCategory.value)}
            value={editingCategory.symbology}
            isPointLayer={isPointLayer}
            onRequestExternalIcon={onRequestExternalIcon}
            onApply={(cfg) => applyToCategory(editingIndex!, cfg)}
            onApplyToAll={applyToAll}
          />
        )}
      </Modal>
    </div>
  );
};

export default UniqueSymbology;
